import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Target, ArrowLeft, Save, Shield, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';

// ★ 2026-08-02 (Fase 30 F4) — Pagina dedicata per la modifica dei target KPI.
// Accesso ristretto a Richard/Eufemia (whitelisted lato backend con 403).

export default function KpiTargetsSettingsPage() {
  const { user } = useAuth() || {};
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ associate: '', senior: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings/kpi-targets');
      setState(data);
      setForm({
        associate: String(data.associate_risposta_to_show_pct ?? ''),
        senior: String(data.senior_show_to_close_pct ?? ''),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento impostazioni');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canEdit = !!state?.can_edit;

  const save = async () => {
    if (!canEdit) return;
    const payload = {};
    const a = parseFloat(form.associate);
    const s = parseFloat(form.senior);
    if (!Number.isNaN(a)) {
      if (a < 0 || a > 100) { toast.error('Il target Associate deve essere tra 0 e 100%'); return; }
      payload.associate_risposta_to_show_pct = a;
    }
    if (!Number.isNaN(s)) {
      if (s < 0 || s > 100) { toast.error('Il target Senior deve essere tra 0 e 100%'); return; }
      payload.senior_show_to_close_pct = s;
    }
    if (Object.keys(payload).length === 0) {
      toast.error('Nessun valore da salvare');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch('/settings/kpi-targets', payload);
      setState(data);
      setForm({
        associate: String(data.associate_risposta_to_show_pct ?? ''),
        senior: String(data.senior_show_to_close_pct ?? ''),
      });
      toast.success('Target KPI aggiornati');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!canEdit || !state?.defaults) return;
    setForm({
      associate: String(state.defaults.associate_risposta_to_show_pct),
      senior: String(state.defaults.senior_show_to_close_pct),
    });
  };

  const isDirty = state && (
    parseFloat(form.associate) !== state.associate_risposta_to_show_pct ||
    parseFloat(form.senior) !== state.senior_show_to_close_pct
  );

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-3xl" data-testid="kpi-targets-settings-page">
      <div className="flex items-center gap-3">
        <Button size="sm" variant="ghost" onClick={() => nav(-1)} data-testid="kpi-targets-back">
          <ArrowLeft className="w-4 h-4 mr-1" />Indietro
        </Button>
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h1 className="text-2xl font-serif text-[hsl(var(--primary))]">Impostazioni target KPI</h1>
        </div>
      </div>

      {!canEdit && (
        <Alert variant="default" className="border-amber-300 bg-amber-50/40" data-testid="kpi-targets-readonly-notice">
          <Shield className="w-4 h-4" />
          <AlertTitle>Solo visualizzazione</AlertTitle>
          <AlertDescription>
            La modifica dei target KPI è riservata agli admin CEO (Richard, Eufemia).
            Il tuo account ({user?.email}) può solo visualizzare i valori correnti.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-serif flex items-center gap-2">
            <Target className="w-4 h-4" />
            Conversion targets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="target-associate" className="text-sm">
                Associate · Risposta → Show
                <Badge variant="outline" className="ml-2 text-[10px]">
                  Default {state?.defaults?.associate_risposta_to_show_pct ?? 45}%
                </Badge>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="target-associate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={form.associate}
                  onChange={(e) => setForm((f) => ({ ...f, associate: e.target.value }))}
                  disabled={!canEdit || saving}
                  className="w-32"
                  data-testid="kpi-target-associate-input"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Percentuale minima di lead che, dopo aver risposto, devono arrivare allo Show.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="target-senior" className="text-sm">
                Senior · Show → Chiuso
                <Badge variant="outline" className="ml-2 text-[10px]">
                  Default {state?.defaults?.senior_show_to_close_pct ?? 8}%
                </Badge>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="target-senior"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={form.senior}
                  onChange={(e) => setForm((f) => ({ ...f, senior: e.target.value }))}
                  disabled={!canEdit || saving}
                  className="w-32"
                  data-testid="kpi-target-senior-input"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Percentuale minima di Show che il Senior deve convertire in Chiuso Cliente/Partner.
              </p>
            </div>
          </div>

          {canEdit && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
              <Button
                onClick={save}
                disabled={!isDirty || saving}
                data-testid="kpi-targets-save"
              >
                <Save className="w-4 h-4 mr-1" />{saving ? 'Salvataggio…' : 'Salva modifiche'}
              </Button>
              <Button
                variant="outline"
                onClick={resetDefaults}
                disabled={saving}
                data-testid="kpi-targets-reset"
              >
                <Undo2 className="w-4 h-4 mr-1" />Ripristina default
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-50/60">
        <CardContent className="p-4">
          <div className="text-xs text-muted-foreground space-y-1">
            <p><strong>Nota tecnica:</strong> i target modificati vengono applicati immediatamente
            in tutta l&apos;applicazione (Classifica, scheda agente, funnel dashboard). Non serve
            rideployare l&apos;app. I valori vengono salvati nel database (collection
            <code className="mx-1 px-1 py-0.5 bg-slate-200 rounded">system_settings</code>)
            e sopravvivono ai restart.</p>
            <p>Se lasci vuoto un campo il valore attuale non viene modificato.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
